import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ExtensionListRowProps {
  selected?: boolean;
  onSelect: (e?: MouseEvent) => void;
  onOpen: (e?: MouseEvent) => void;
  onContextMenu: (e: MouseEvent) => void;
  children: ReactNode;
  trailing?: ReactNode;
  className?: string;
  testId?: string;
}

function isNestedRowAction(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('[data-row-action]'));
}

export function ExtensionListRow({
  selected,
  onSelect,
  onOpen,
  onContextMenu,
  children,
  trailing,
  className,
  testId,
}: ExtensionListRowProps) {
  const onRowContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    onContextMenu(e);
  };

  return (
    <div
      data-testid={testId}
      data-selected={selected ? 'true' : undefined}
      className={cn(
        'group relative flex w-full items-start border-b border-border/60 hover:bg-accent/40',
        // Full accent wash (stronger than hover) + 2px rail. Do not use
        // border-l-primary (it recolors border-b into a box). bg-primary/8
        // disappears on Paper.
        selected &&
          'bg-accent hover:bg-accent before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-primary',
        className,
      )}
      aria-selected={selected || undefined}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (isNestedRowAction(e.target)) return;
          onSelect(e);
        }}
        onDoubleClick={(e) => {
          if (isNestedRowAction(e.target)) return;
          onOpen(e);
        }}
        onContextMenu={onRowContextMenu}
        onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 px-6 py-3.5 text-left"
      >
        {children}
      </div>
      {trailing ? (
        <div className="flex shrink-0 items-center gap-1 self-center pr-4">{trailing}</div>
      ) : null}
    </div>
  );
}
